import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  IconButton,
  Autocomplete,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Alert,
  useTheme,
  useMediaQuery,
  CircularProgress,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import {
  Search as SearchIcon,
  SwapHoriz as SwapIcon,
  Add as AddIcon,
  RequestPage as RequestIcon,
  DriveEta as DriverIcon,
  Person as PassengerIcon,
  History as HistoryIcon,
  Close as CloseIcon,
  ArrowForward as ArrowForwardIcon,
  Security as SecurityIcon,
  Speed as SpeedIcon,
  LocalFlorist as EcoIcon,
  Support as SupportIcon,
  Android as AndroidIcon,
  Apple as AppleIcon,
  Twitter as TwitterIcon,
  LinkedIn as LinkedInIcon,
  Instagram as InstagramIcon,
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import apiService from '../services/apiService';

interface RecentSearch {
  id: string;
  originCity: any;
  destinationCity: any;
  timestamp: number;
}

const RECENT_SEARCHES_KEY = 'rideclub_recent_searches';
const MAX_RECENT_SEARCHES = 5;

const HomePage: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const navigate = useNavigate();
  const { user } = useAuth();

  // Search form state
  const [originCity, setOriginCity] = useState<any>(null);
  const [destinationCity, setDestinationCity] = useState<any>(null);
  const [departureDate, setDepartureDate] = useState<Date | null>(new Date());
  const [anyDate, setAnyDate] = useState<boolean>(false);
  const [passengers, setPassengers] = useState<number>(1);
  const [searchError, setSearchError] = useState('');
  
  // City search state
  const [originCities, setOriginCities] = useState<any[]>([]);
  const [destinationCities, setDestinationCities] = useState<any[]>([]);
  const [originLoading, setOriginLoading] = useState(false);
  const [destinationLoading, setDestinationLoading] = useState(false);

  // Recent searches state
  const [recentSearches, setRecentSearches] = useState<RecentSearch[]>([]);

  // Load recent searches from localStorage on mount
  useEffect(() => {
    const savedSearches = localStorage.getItem(RECENT_SEARCHES_KEY);
    if (savedSearches) {
      try {
        setRecentSearches(JSON.parse(savedSearches));
      } catch (error) {
        console.error('Error loading recent searches:', error);
      }
    }
  }, []);

  // Search cities from API
  const searchCities = async (query: string, setter: (cities: any[]) => void, setLoading: (loading: boolean) => void) => {
    if (!query || query.length < 2) {
      setter([]);
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.get('/locations/cities/search', { q: query, limit: 10 });
      if (response.success) {
        setter(response.data?.cities || []);
      }
    } catch (err) {
      console.error('City search error:', err);
      setter([]);
    } finally {
      setLoading(false);
    }
  };

  // Save search to recent searches
  const saveRecentSearch = (origin: any, destination: any) => {
    if (!origin || !destination) return;

    const newSearch: RecentSearch = {
      id: `${origin.id}-${destination.id}-${Date.now()}`,
      originCity: origin,
      destinationCity: destination,
      timestamp: Date.now(),
    };

    // Remove duplicate route if exists
    const filtered = recentSearches.filter(
      (search) => !(search.originCity.id === origin.id && search.destinationCity.id === destination.id)
    );

    // Add new search at the beginning and keep only last 5
    const updated = [newSearch, ...filtered].slice(0, MAX_RECENT_SEARCHES);
    
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  // Delete a recent search
  const deleteRecentSearch = (searchId: string) => {
    const updated = recentSearches.filter((search) => search.id !== searchId);
    setRecentSearches(updated);
    localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(updated));
  };

  // Load search from recent
  const loadRecentSearch = (search: RecentSearch) => {
    setOriginCity(search.originCity);
    setDestinationCity(search.destinationCity);
  };

  const handleSearchRides = () => {
    setSearchError('');

    if (!originCity || !destinationCity) {
      setSearchError(t('homePage.searchForm.errors.selectCities'));
      return;
    }

    if (originCity.id === destinationCity.id) {
      setSearchError(t('homePage.searchForm.errors.sameCity'));
      return;
    }

    // Save to recent searches
    saveRecentSearch(originCity, destinationCity);

    navigate('/find-rides', {
      state: {
        originCity,
        destinationCity,
        departureDate: anyDate ? null : departureDate,
        passengers,
      }
    });
  };

  const handleSwapCities = () => {
    const temp = originCity;
    setOriginCity(destinationCity);
    setDestinationCity(temp);
  };

  const handleActionClick = (action: string) => {
    if (!user) {
      // All users register the same way now
      navigate('/register');
      return;
    }

    switch (action) {
      case 'request-ride':
        navigate('/request-ride');
        break;
      case 'offer-ride':
        navigate('/create-ride');
        break;
      case 'become-driver':
        navigate('/become-driver'); // Redirect to driver verification page
        break;
      case 'become-passenger':
        // Already a passenger by default, do nothing or show message
        break;
    }
  };

  return (
    <LocalizationProvider dateAdapter={AdapterDateFns}>
      <Box
        sx={{
          width: '100%',
          minHeight: '100vh',
          background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
          position: 'relative',
          pt: { xs: 8, md: 10 },
          pb: { xs: 6, md: 8 },
        }}
      >
        {/* Hero Section */}
        <Box
          sx={{
            width: '100%',
            maxWidth: { xs: '95%', sm: '90%', md: '1200px' },
            mx: 'auto',
            px: { xs: 2, sm: 3, md: 4 },
          }}
        >
          {/* Heading */}
          <Box sx={{ textAlign: 'center', mb: 8 }}>
            <Typography
              variant="h1"
              sx={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontWeight: 900,
                fontSize: { xs: '10rem', sm: '14rem', md: '18rem' },
                color: '#1a1a1a',
                mb: 4,
                lineHeight: 1,
                letterSpacing: '-0.03em',
              }}
            >
              {t('homePage.title')}
            </Typography>
            <Typography
              variant="body1"
              sx={{
                fontFamily: 'Inter, system-ui, sans-serif',
                fontSize: { xs: '3.5rem', md: '4.5rem' },
                color: '#4b5563',
                lineHeight: 1.4,
                fontWeight: 600,
              }}
            >
              {t('homePage.subtitle')}
            </Typography>
          </Box>

          {/* Main Card - Search Form + Action Buttons */}
          <Box
            sx={{
              backgroundColor: '#ffffff',
              borderRadius: '24px',
              p: { xs: 8, sm: 10, md: 12 },
              boxShadow: '0 20px 40px rgba(0, 0, 0, 0.12)',
              mb: 8,
            }}
          >
            {/* Search Form */}
            <Box
              sx={{
                mb: 5,
              }}
            >
              {searchError && (
                <Alert severity="error" sx={{ mb: 3, borderRadius: 2, fontSize: '1.1rem' }}>
                  {searchError}
                </Alert>
              )}

              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3.5 }}>
                {/* From - Swap - To */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  alignItems: 'center',
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Box sx={{ flex: 1, width: '100%' }}>
                    <Autocomplete
                      options={originCities}
                      getOptionLabel={(option) => `${option.name}, ${option.province}`}
                      value={originCity}
                      onChange={(event, newValue) => setOriginCity(newValue)}
                      onInputChange={(event, value) => searchCities(value, setOriginCities, setOriginLoading)}
                      loading={originLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('homePage.searchForm.fromLabel')}
                          placeholder={t('homePage.searchForm.searchPlaceholder')}
                          fullWidth
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {originLoading ? <CircularProgress size={48} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                            sx: { fontSize: '3.6rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '3.6rem' }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '18px',
                              '& input': {
                                padding: '42px 32px',
                                fontSize: '3.6rem',
                              }
                            },
                          }}
                        />
                      )}
                    />
                  </Box>

                  <IconButton
                    onClick={handleSwapCities}
                    disabled={!originCity || !destinationCity}
                    sx={{
                      bgcolor: '#4CAF50',
                      color: 'white',
                      width: 100,
                      height: 100,
                      '&:hover': { bgcolor: '#45a049' },
                      '&:disabled': { bgcolor: '#e5e7eb' },
                    }}
                  >
                    <SwapIcon sx={{ fontSize: 60 }} />
                  </IconButton>

                  <Box sx={{ flex: 1, width: '100%' }}>
                    <Autocomplete
                      options={destinationCities}
                      getOptionLabel={(option) => `${option.name}, ${option.province}`}
                      value={destinationCity}
                      onChange={(event, newValue) => setDestinationCity(newValue)}
                      onInputChange={(event, value) => searchCities(value, setDestinationCities, setDestinationLoading)}
                      loading={destinationLoading}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label={t('homePage.searchForm.toLabel')}
                          placeholder={t('homePage.searchForm.searchPlaceholder')}
                          fullWidth
                          variant="outlined"
                          InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                              <>
                                {destinationLoading ? <CircularProgress size={48} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                            sx: { fontSize: '3.6rem' }
                          }}
                          InputLabelProps={{
                            sx: { fontSize: '3.6rem' }
                          }}
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '18px',
                              '& input': {
                                padding: '42px 32px',
                                fontSize: '3.6rem',
                              }
                            },
                          }}
                        />
                      )}
                    />
                  </Box>
                </Box>

                {/* Date - Passengers */}
                <Box sx={{
                  display: 'flex',
                  gap: 2,
                  flexDirection: { xs: 'column', sm: 'row' }
                }}>
                  <Box sx={{ flex: 1 }}>
                    <DatePicker
                      label={t('homePage.searchForm.departureDateLabel')}
                      value={departureDate}
                      onChange={(newValue) => setDepartureDate(newValue)}
                      minDate={new Date()}
                      disabled={anyDate}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          variant: 'outlined',
                          InputProps: {
                            sx: { fontSize: '3.6rem' }
                          },
                          InputLabelProps: {
                            sx: { fontSize: '3.6rem' }
                          },
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              borderRadius: '18px',
                              '& input': {
                                padding: '42px 32px',
                                fontSize: '3.6rem',
                              }
                            },
                          }
                        },
                      }}
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={anyDate}
                          onChange={(e) => setAnyDate(e.target.checked)}
                          size="medium"
                          sx={{
                            '& .MuiSvgIcon-root': { fontSize: 50 }
                          }}
                        />
                      }
                      label={t('homePage.searchForm.anyDateLabel')}
                      sx={{ 
                        mt: 2, 
                        ml: 0,
                        '& .MuiTypography-root': {
                          fontSize: '3rem'
                        }
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <FormControl fullWidth variant="outlined">
                      <InputLabel sx={{ fontSize: '3.6rem' }}>{t('homePage.searchForm.passengersLabel')}</InputLabel>
                      <Select
                        value={passengers}
                        label={t('homePage.searchForm.passengersLabel')}
                        onChange={(e) => setPassengers(e.target.value as number)}
                        sx={{
                          borderRadius: '18px',
                          fontSize: '3.6rem',
                          '& .MuiSelect-select': {
                            padding: '42px 32px',
                          }
                        }}
                      >
                        {[1, 2, 3, 4, 5, 6, 7, 8].map((num) => (
                          <MenuItem key={num} value={num} sx={{ fontSize: '3.6rem' }}>
                            {num} {t(num === 1 ? 'homePage.searchForm.passenger' : 'homePage.searchForm.passengers')}
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </Box>
                </Box>

                {/* Search Button */}
                <Button
                  fullWidth
                  variant="contained"
                  size="large"
                  onClick={handleSearchRides}
                  startIcon={<SearchIcon sx={{ fontSize: 60 }} />}
                  sx={{
                    py: 5,
                    fontSize: '3.8rem',
                    fontWeight: 800,
                    fontFamily: 'Inter, sans-serif',
                    backgroundColor: '#4CAF50',
                    borderRadius: '18px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#45a049',
                    },
                  }}
                >
                  {t('homePage.searchForm.searchButton')}
                </Button>

                {/* Recent Searches */}
                {recentSearches.length > 0 && (
                  <Box sx={{ mt: 3 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <HistoryIcon sx={{ fontSize: 22, color: 'text.secondary', mr: 1 }} />
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 600, fontSize: '1.1rem' }}>
                        {t('homePage.recentSearches')}
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      {recentSearches.map((search) => (
                        <Box
                          key={search.id}
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            p: 2,
                            border: '1px solid #e5e7eb',
                            borderRadius: '10px',
                            cursor: 'pointer',
                            transition: 'all 0.2s',
                            '&:hover': {
                              backgroundColor: '#f9fafb',
                              borderColor: '#4CAF50',
                            },
                          }}
                          onClick={() => loadRecentSearch(search)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flex: 1 }}>
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                              {search.originCity.name}
                            </Typography>
                            <ArrowForwardIcon sx={{ fontSize: 20, color: 'text.secondary' }} />
                            <Typography variant="body2" sx={{ fontWeight: 600, fontSize: '1.1rem' }}>
                              {search.destinationCity.name}
                            </Typography>
                          </Box>
                          <IconButton
                            size="medium"
                            onClick={(e) => {
                              e.stopPropagation();
                              deleteRecentSearch(search.id);
                            }}
                            sx={{
                              color: 'text.secondary',
                              '&:hover': {
                                color: 'error.main',
                                backgroundColor: 'error.light',
                              },
                            }}
                          >
                            <CloseIcon fontSize="medium" />
                          </IconButton>
                        </Box>
                      ))}
                    </Box>
                  </Box>
                )}
              </Box>
            </Box>

            {/* Action Buttons - Horizontal Row */}
            <Box
              sx={{
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 4, sm: 4 },
                flexWrap: 'wrap',
                mt: 6,
              }}
            >
              <Box
                onClick={() => handleActionClick('request-ride')}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 auto' },
                  backgroundColor: '#f8fafc',
                  border: '3px solid #e5e7eb',
                  borderRadius: '20px',
                  p: { xs: 6, md: 8 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '160px', md: '200px' },
                  '&:hover': {
                    borderColor: '#4CAF50',
                    backgroundColor: '#f0fdf4',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: { xs: '3.2rem', md: '3.8rem' },
                    color: '#1a1a1a',
                    textAlign: 'center',
                  }}
                >
                  {t('homePage.actions.requestRide')}
                </Typography>
              </Box>

              <Box
                onClick={() => handleActionClick('offer-ride')}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 auto' },
                  backgroundColor: '#f8fafc',
                  border: '3px solid #e5e7eb',
                  borderRadius: '20px',
                  p: { xs: 6, md: 8 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '160px', md: '200px' },
                  '&:hover': {
                    borderColor: '#4CAF50',
                    backgroundColor: '#f0fdf4',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: { xs: '3.2rem', md: '3.8rem' },
                    color: '#1a1a1a',
                    textAlign: 'center',
                  }}
                >
                  {t('homePage.actions.offerRide')}
                </Typography>
              </Box>

              <Box
                onClick={() => handleActionClick('become-driver')}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 auto' },
                  backgroundColor: '#f8fafc',
                  border: '3px solid #e5e7eb',
                  borderRadius: '20px',
                  p: { xs: 6, md: 8 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '160px', md: '200px' },
                  '&:hover': {
                    borderColor: '#4CAF50',
                    backgroundColor: '#f0fdf4',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: { xs: '3.2rem', md: '3.8rem' },
                    color: '#1a1a1a',
                    textAlign: 'center',
                  }}
                >
                  {t('homePage.actions.becomeDriver')}
                </Typography>
              </Box>

              <Box
                onClick={() => handleActionClick('become-passenger')}
                sx={{
                  flex: { xs: '1 1 100%', sm: '1 1 calc(50% - 16px)', md: '1 1 auto' },
                  backgroundColor: '#f8fafc',
                  border: '3px solid #e5e7eb',
                  borderRadius: '20px',
                  p: { xs: 6, md: 8 },
                  cursor: 'pointer',
                  transition: 'all 0.2s ease',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: { xs: '160px', md: '200px' },
                  '&:hover': {
                    borderColor: '#4CAF50',
                    backgroundColor: '#f0fdf4',
                    transform: 'scale(1.05)',
                    boxShadow: '0 8px 20px rgba(76, 175, 80, 0.3)',
                  },
                }}
              >
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontFamily: 'Inter, system-ui, sans-serif',
                    fontSize: { xs: '3.2rem', md: '3.8rem' },
                    color: '#1a1a1a',
                    textAlign: 'center',
                  }}
                >
                  {t('homePage.actions.becomePassenger')}
                </Typography>
              </Box>
            </Box>
          </Box>
        </Box>

        {/* Footer Section */}
        <Box
          component="footer"
          sx={{
            mt: 12,
            py: 10,
            px: { xs: 4, md: 12 },
            borderTop: '1px solid rgba(0,0,0,0.1)',
            backgroundColor: 'rgba(255,255,255,0.5)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <Box
            sx={{
              maxWidth: '1200px',
              mx: 'auto',
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(4, 1fr)' },
              gap: 8,
            }}
          >
            {/* Company */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, fontSize: '1.8rem' }}>
                {t('homePage.footer.company.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.company.about')}
                </Typography>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.company.contact')}
                </Typography>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.company.safety')}
                </Typography>
              </Box>
            </Box>

            {/* Resources */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, fontSize: '1.8rem' }}>
                {t('homePage.footer.resources.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.resources.howToBeADriver')}
                </Typography>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.resources.howToBeAPassenger')}
                </Typography>
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1,
                    cursor: 'pointer',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  <SupportIcon />
                  <Typography sx={{ fontSize: '1.4rem' }}>{t('homePage.footer.resources.support')}</Typography>
                </Box>
              </Box>
            </Box>

            {/* Legal */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, fontSize: '1.8rem' }}>
                {t('homePage.footer.legal.title')}
              </Typography>
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.legal.terms')}
                </Typography>
                <Typography
                  sx={{
                    cursor: 'pointer',
                    fontSize: '1.4rem',
                    '&:hover': { color: '#4CAF50' },
                  }}
                >
                  {t('homePage.footer.legal.privacy')}
                </Typography>
              </Box>
            </Box>

            {/* Apps & Social */}
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 800, mb: 3, fontSize: '1.8rem' }}>
                {t('homePage.footer.getTheApp')}
              </Typography>
              <Box sx={{ display: 'flex', gap: 3, mb: 4 }}>
                <IconButton
                  sx={{
                    bgcolor: '#000',
                    color: '#fff',
                    '&:hover': { bgcolor: '#333' },
                  }}
                >
                  <AndroidIcon sx={{ fontSize: 30 }} />
                </IconButton>
                <IconButton
                  sx={{
                    bgcolor: '#000',
                    color: '#fff',
                    '&:hover': { bgcolor: '#333' },
                  }}
                >
                  <AppleIcon sx={{ fontSize: 30 }} />
                </IconButton>
              </Box>
              <Box sx={{ display: 'flex', gap: 2 }}>
                <TwitterIcon
                  sx={{ fontSize: 24, cursor: 'pointer', color: '#555' }}
                />
                <LinkedInIcon
                  sx={{ fontSize: 24, cursor: 'pointer', color: '#555' }}
                />
                <InstagramIcon
                  sx={{ fontSize: 24, cursor: 'pointer', color: '#555' }}
                />
              </Box>
            </Box>
          </Box>
          <Typography
            sx={{
              textAlign: 'center',
              mt: 10,
              color: '#666',
              fontSize: '1.2rem',
            }}
          >
            {t('homePage.footer.copyright', { year: new Date().getFullYear() })}
          </Typography>
        </Box>
      </Box>
    </LocalizationProvider>
  );
};

export default HomePage;
